/**
 * @fileoverview Label line above a form control: text, required marker, the lock
 * or derived mark, and the help overlay. Consumed by `FieldUI` and by any control
 * that renders its own caption.
 *
 * Memoized because it sits on the keystroke path: every character typed into a
 * field re-renders `FieldUI`, and without the bail-out the whole antd overlay
 * subtree re-renders with it. Every prop is a primitive derived from the *field
 * type* or from the derive config — never from `uiField` — so they stay
 * shallow-equal across a keystroke; an inline object or closure here would
 * un-memoize the label. This component reads no React context.
 *
 * A derived field renders the `fx` mark INSTEAD of the padlock, even when it is
 * also protected (SYD-2404 §8): one mark per field, and the popover then carries
 * both sentences. The mark is a bare glyph in the same quaternary colour as the
 * padlock it replaces — no border, no box, because nothing else in a field label
 * is boxed. The mark is not a lock, because permission is not the reason. That
 * popover REPLACES the plain tooltip rather than nesting inside it —
 * two overlays on one label open together on hover — and it is reached by keyboard
 * through the focusable mark. The mark is static: it never blinks and never changes
 * width. While a computation is live it is the value control that pulses, through
 * the shared `calculating-value` class `FieldUI` puts on `ControlUI`, and the value
 * on screen is still the last correct answer.
 *
 * The `data-pdf-label` attribute is emitted unconditionally, not gated on export
 * mode: the only CSS that reads it (`PDF_EXPORT_STYLE_OVERRIDES`) is injected by
 * the PDF worker, and a context read here would defeat the memo bail-out. Overlay
 * content is absent from the DOM until it opens, so it never prints.
 */

import { FC, ReactNode, memo } from 'react';
import { Typography, Tooltip, Popover, theme } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { formDeriveCopy } from '@/services/formDerives';
import FlexCol from './FlexCol';

const { Text } = Typography;

interface FieldLabelProps {
  label: ReactNode;
  required?: boolean;
  tooltip?: ReactNode;
  isProtected?: boolean;
  /** A Form Derived Value writes this field: show the `fx` mark, never the lock. */
  isDerived?: boolean;
  /** Where the value comes from, in the same sentence the Derived values list shows. */
  derivedSentence?: string;
  /** Ready-made `Last calculated …` line; omitted when the derive has no stamp. */
  derivedComputedAtLabel?: string;
  /** The derive is not being kept up to date; the reason stays out of the form. */
  derivedSkipped?: boolean;
  /** The derived value is empty, so the control shows the no-value placeholder. */
  derivedNoValue?: boolean;
}

const FieldLabel: FC<FieldLabelProps> = memo(
  ({
    label,
    required,
    tooltip,
    isProtected,
    isDerived,
    derivedSentence,
    derivedComputedAtLabel,
    derivedSkipped,
    derivedNoValue,
  }) => {
    const { token } = theme.useToken();
    const protectedNote = 'Protected: Only modifiable by backend functions.';

    const getTooltipTitle = () => {
      const notes: string[] = [];
      if (tooltip) notes.push(String(tooltip));
      if (isProtected) notes.push(protectedNote);
      return notes.length > 0 ? notes.join('. ') : undefined;
    };

    const markStyle = {
      marginLeft: token.marginXXS,
      color: token.colorTextQuaternary,
    };

    const derivedMarkStyle = {
      ...markStyle,
      fontSize: token.fontSizeSM,
      cursor: 'help',
    };

    const labelContent = (
      <Text data-pdf-label="true">
        {label}
        {required && (
          <Text
            style={{
              color: token.colorTextQuaternary,
            }}
          >
            *
          </Text>
        )}
        {isProtected && !isDerived && (
          <LockOutlined aria-label="Protected field" style={markStyle} />
        )}
        {isDerived && (
          <Text
            tabIndex={0}
            aria-label="Derived value"
            style={derivedMarkStyle}
            italic
          >
            fx
          </Text>
        )}
      </Text>
    );

    const tooltipTitle = getTooltipTitle();

    /**
     * Spec §8 note 2 and §10: what it is, what it is calculated from, then the facts
     * that qualify it. The field's own help text leads, because this overlay is the
     * only one a derived label has. The protected note is here rather than on a
     * padlock — that glyph is suppressed on a derived field.
     */
    if (isDerived) {
      return (
        <Popover
          title={formDeriveCopy.fieldPopoverTitle}
          trigger={['hover', 'focus']}
          content={
            <FlexCol gap={5} noGrow>
              {tooltip && <Text>{tooltip}</Text>}
              {derivedSentence && (
                <Text>{formDeriveCopy.fieldPopoverBody(derivedSentence)}</Text>
              )}
              {derivedNoValue && <Text>{formDeriveCopy.noValueBody}</Text>}
              {derivedSkipped && <Text>{formDeriveCopy.skippedToFiller}</Text>}
              {isProtected && <Text>{protectedNote}</Text>}
              {derivedComputedAtLabel && (
                <Text type="secondary">{derivedComputedAtLabel}</Text>
              )}
            </FlexCol>
          }
        >
          {labelContent}
        </Popover>
      );
    }

    return tooltipTitle ? (
      <Tooltip title={tooltipTitle}>{labelContent}</Tooltip>
    ) : (
      labelContent
    );
  },
);

FieldLabel.displayName = 'FieldLabel';

export default FieldLabel;
